/** Representative frame thumbnail via input-video seek — zero extra R2 uploads. */

import { UserOutlined } from '@ant-design/icons'
import { memo, useEffect, useRef, useState } from 'react'

type Props = {
  videoUrl: string
  seekMs?: number
  alt: string
  className?: string
}

function MotionTrackVideoThumbImpl({ videoUrl, seekMs = 0, alt, className }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [thumbUrl, setThumbUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setThumbUrl(null)
    setFailed(false)
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    videoRef.current = video

    const capture = () => {
      const canvas = canvasRef.current || document.createElement('canvas')
      canvasRef.current = canvas
      const w = video.videoWidth || 160
      const h = video.videoHeight || 90
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        setFailed(true)
        return
      }
      ctx.drawImage(video, 0, 0, w, h)
      try {
        setThumbUrl(canvas.toDataURL('image/jpeg', 0.72))
      } catch {
        setFailed(true)
      }
    }

    const onLoaded = () => {
      const t = Math.max(0, (seekMs || 0) / 1000)
      if (Number.isFinite(video.duration) && video.duration > 0) {
        video.currentTime = Math.min(t, Math.max(0, video.duration - 0.05))
      } else {
        video.currentTime = t
      }
    }

    video.addEventListener('loadeddata', onLoaded)
    video.addEventListener('seeked', capture, { once: true })
    video.addEventListener('error', () => setFailed(true), { once: true })
    video.src = videoUrl

    return () => {
      video.removeEventListener('loadeddata', onLoaded)
      video.removeEventListener('seeked', capture)
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }, [seekMs, videoUrl])

  if (thumbUrl) {
    return <img src={thumbUrl} alt={alt} className={className} />
  }

  return (
    <div className={`pcv-motion-track-thumb-fallback${className ? ` ${className}` : ''}`} aria-hidden>
      <UserOutlined />
      {failed ? null : <span className="pcv-motion-track-thumb-loading" />}
    </div>
  )
}

export default memo(MotionTrackVideoThumbImpl)
